import is from '@sindresorhus/is';
import clsx from 'clsx';
import { type FunctionComponent, type ReactElement, useId, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { Translate } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { IconCircleCheck, IconPencil, IconWarning } from '~/ui/core/icons';
import { Input } from '~/ui/core/input/Input';
import { RichEditor } from '~/ui/core/rich-editor/RichEditor';
import { Timeline } from '~/ui/core/timeline/Timeline';

import type { GrowthCheckpoint, GrowthCheckpointMark, GrowthGoal } from '../../data-contract/growth';
import { useGoalAction } from '../api/growth-mutations';
import { ChoiceButtons } from '../components/ChoiceButtons';
import { DetailSection } from '../components/GrowthTileFrame';
import { isPastDue, markLabel, nextOpenCheckpoint, useFormatDay } from '../components/goal-display';

const MARK_ORDER: readonly GrowthCheckpointMark[] = ['on-track', 'needs-support', 'changed'];

const unitFor = (goal: GrowthGoal): string | undefined => {
    if (goal.measure?.type === 'number') {
        return goal.measure.unit;
    }

    return goal.measure?.type === 'percent' ? '%' : undefined;
};

const CheckpointForm: FunctionComponent<{
    readonly checkpoint?: GrowthCheckpoint;
    readonly goal: GrowthGoal;
    readonly onDone: () => void;
}> = ({ checkpoint, goal, onDone }) => {
    const translate = useTranslate();
    const goalAction = useGoalAction(goal.id);
    const unit = unitFor(goal);
    const goalValue = goal.measure?.type === 'number' || goal.measure?.type === 'percent' ? goal.measure.current : 0;
    const currentValue = checkpoint?.value ?? goalValue;
    const [value, setValue] = useState(String(currentValue));
    const [mark, setMark] = useState<GrowthCheckpointMark>('on-track');
    const [note, setNote] = useState('');
    const valueId = useId();
    const parsedValue = Number(value.replace(',', '.'));
    const canSave =
        !goalAction.isPending && (unit === undefined || (value.trim().length > 0 && Number.isFinite(parsedValue)));

    const save = (): void =>
        goalAction.mutate(
            {
                action: 'update-checkpoint',
                checkpointId: checkpoint?.id ?? null,
                mark,
                note,
                value: unit === undefined ? null : parsedValue,
            },
            { onSuccess: onDone },
        );

    return (
        <div className="mt-m flex w-full flex-col gap-xl rounded-xl border border-border-subtle bg-surface-raised p-xl shadow-sm">
            <div className="flex flex-wrap items-end gap-xl">
                {unit === undefined ? null : (
                    <div className="flex w-[9rem] flex-col gap-s">
                        <label className="text-body-xs font-medium text-text-muted" htmlFor={valueId}>
                            {translate('pages.growth.checkpoint.value-label')}
                        </label>
                        <Input
                            endElement={<span className="text-body-s text-text-muted">{unit}</span>}
                            id={valueId}
                            inputClassName="tabular-nums"
                            inputMode="decimal"
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                        />
                    </div>
                )}
                <div className="flex flex-col gap-s">
                    <span className="text-body-xs font-medium text-text-muted">
                        {translate('pages.growth.checkpoint.mark-label')}
                    </span>
                    <ChoiceButtons<GrowthCheckpointMark>
                        ariaLabel={translate('pages.growth.checkpoint.mark-label')}
                        options={MARK_ORDER.map((value) => ({ label: markLabel(value, translate), value }))}
                        value={mark}
                        onChange={setMark}
                    />
                </div>
            </div>
            <div className="overflow-hidden rounded-sm border border-border-control">
                <RichEditor
                    changeDebounceMs={0}
                    ariaLabel={translate('pages.growth.checkpoint.note-label')}
                    contentClassName="min-h-0!"
                    editableClassName="min-h-[3.5rem]"
                    placeholder={translate('pages.growth.checkpoint.note-placeholder')}
                    value={note}
                    onChange={setNote}
                />
            </div>
            <div className="flex justify-end gap-s">
                <Button size="sm" variant="tertiary" onClick={onDone}>
                    {translate('common.cancel')}
                </Button>
                <Button disabled={!canSave} size="sm" variant="primary" onClick={save}>
                    {translate('common.save')}
                </Button>
            </div>
        </div>
    );
};

const markIcons: Record<GrowthCheckpointMark, ReactElement> = {
    changed: <IconPencil />,
    'needs-support': <IconWarning />,
    'on-track': <IconCircleCheck />,
};

const UpcomingIcon: FunctionComponent<{ readonly isOverdue: boolean }> = ({ isOverdue }) => (
    <span
        className={clsx(
            'size-xl rounded-full border-[1.5px]',
            isOverdue ? 'border-status-error-text' : 'border-border-strong',
        )}
    />
);

const doneTitle = (checkpoint: GrowthCheckpoint, translate: Translate): string =>
    [
        checkpoint.mark ? markLabel(checkpoint.mark, translate) : translate('pages.growth.states.done'),
        is.number(checkpoint.value) ? String(checkpoint.value) : undefined,
    ]
        .filter(is.string)
        .join(' · ');

export const CheckpointsSection: FunctionComponent<{ readonly canUpdate: boolean; readonly goal: GrowthGoal }> = ({
    canUpdate,
    goal,
}) => {
    const translate = useTranslate();
    const formatDay = useFormatDay();
    const [isUpdating, setIsUpdating] = useState(false);
    const [isCheckingIn, setIsCheckingIn] = useState(false);
    const next = nextOpenCheckpoint(goal);
    const upcoming = goal.checkpoints.filter(({ doneAt }) => is.nullOrUndefined(doneAt));
    const done = goal.checkpoints.filter(({ doneAt }) => is.string(doneAt)).toReversed();

    if (goal.checkpoints.length === 0 && !canUpdate) {
        return null;
    }

    return (
        <DetailSection title={translate('pages.growth.checkpoint.title')}>
            {canUpdate ? (
                isCheckingIn ? (
                    <CheckpointForm goal={goal} onDone={() => setIsCheckingIn(false)} />
                ) : (
                    <div className="flex items-center justify-between gap-m">
                        {goal.checkpoints.length === 0 ? (
                            <span className="text-body-s text-text-muted">
                                {translate('pages.growth.checkpoint.empty')}
                            </span>
                        ) : (
                            <span />
                        )}
                        <Button size="sm" variant="secondary" onClick={() => setIsCheckingIn(true)}>
                            {translate('pages.growth.checkpoint.check-in')}
                        </Button>
                    </div>
                )
            ) : null}
            <div className="flex flex-col">
                {upcoming.length > 0 ? (
                    <Timeline.Group
                        isLastGroup={done.length === 0}
                        label={translate('pages.growth.checkpoint.upcoming')}
                    >
                        {upcoming.map((checkpoint) => {
                            const isOverdue = isPastDue(checkpoint.due);
                            const isNext = canUpdate && checkpoint.id === next?.id;

                            return (
                                <Timeline.Item
                                    key={checkpoint.id}
                                    action={
                                        isNext && !isUpdating ? (
                                            <Button size="sm" variant="secondary" onClick={() => setIsUpdating(true)}>
                                                {translate('pages.growth.checkpoint.update')}
                                            </Button>
                                        ) : undefined
                                    }
                                    icon={<UpcomingIcon isOverdue={isOverdue} />}
                                    itemTitle={
                                        isOverdue
                                            ? translate('pages.growth.checkpoint.overdue', {
                                                  date: formatDay(checkpoint.due),
                                              })
                                            : translate('pages.growth.checkpoint.due', {
                                                  date: formatDay(checkpoint.due),
                                              })
                                    }
                                >
                                    {isNext && isUpdating ? (
                                        <CheckpointForm
                                            checkpoint={checkpoint}
                                            goal={goal}
                                            onDone={() => setIsUpdating(false)}
                                        />
                                    ) : null}
                                </Timeline.Item>
                            );
                        })}
                    </Timeline.Group>
                ) : null}
                {done.length > 0 ? (
                    <Timeline.Group isLastGroup={true} label={translate('pages.growth.checkpoint.done')}>
                        {done.map((checkpoint) => (
                            <Timeline.Item
                                key={checkpoint.id}
                                icon={checkpoint.mark ? markIcons[checkpoint.mark] : <IconCircleCheck />}
                                itemTitle={doneTitle(checkpoint, translate)}
                            >
                                {is.nonEmptyString(checkpoint.note) ? (
                                    <RichEditor
                                        ariaLabel={doneTitle(checkpoint, translate)}
                                        contentClassName="min-h-0! bg-transparent p-0! [&_.editor-paragraph:last-child]:mb-0"
                                        disabled={true}
                                        editableClassName="min-h-0 text-body-s text-text-secondary"
                                        toolbar={null}
                                        value={checkpoint.note}
                                    />
                                ) : null}
                                <Timeline.Footer>{formatDay(checkpoint.doneAt ?? checkpoint.due)}</Timeline.Footer>
                            </Timeline.Item>
                        ))}
                    </Timeline.Group>
                ) : null}
            </div>
        </DetailSection>
    );
};
